import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { LANGUAGES, baseLang, rememberLanguage } from "../i18n";
import { prefixFor, stripLangPrefix } from "../i18n/LangContext";
import "../styles/LanguageSwitcher.css";

/**
 * "Orbit" language switcher: the active flag sits in a glowing planet; hover or tap and the
 * other flags swing out of orbit. Picking one spins the planet and pops a greeting bubble.
 */
const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const current = baseLang(i18n.language);
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [bubble, setBubble] = useState(null);
  const rootRef = useRef(null);
  const timers = useRef([]);

  // close when clicking / tapping outside
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const choose = (code) => {
    setOpen(false);
    if (code === current) return;
    rememberLanguage(code);
    // the URL carries the language (/hu/..., /sr/...); LangLayout switches i18next when the route changes
    // A page that exists in fewer languages (the DIMOP page, a Hungarian-only post) lists only
    // those in its hreflang tags. Without a twin, go to that language's blog or home page.
    const stripped = stripLangPrefix(location.pathname);
    const hasTwin = document.head.querySelector(`link[rel="alternate"][hreflang="${code}"]`);
    const path = hasTwin ? stripped : stripped.startsWith("/stories/") ? "/stories" : "/";
    navigate(`${prefixFor(code)}${path === "/" && prefixFor(code) ? "" : path}${location.search}${location.hash}` || "/");
    i18n.changeLanguage(code).then(() => {
      setSpinning(true);
      setBubble(i18n.t("lang.greeting", { lng: code }));
      timers.current.push(setTimeout(() => setSpinning(false), 700));
      timers.current.push(setTimeout(() => setBubble(null), 1600));
    });
  };

  const active = LANGUAGES.find((l) => l.code === current) || LANGUAGES[0];
  const others = LANGUAGES.filter((l) => l.code !== current);
  const names = t("lang.names");

  return (
    <div
      ref={rootRef}
      className={`lang-orbit ${open ? "open" : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {bubble && <span className="lang-bubble" aria-live="polite">{bubble}</span>}

      <div className="lang-orbit-track" role="group" aria-label={t("lang.label")}>
        {others.map((l, i) => (
          <button
            key={l.code}
            type="button"
            className={`lang-moon lang-moon-${i}`}
            style={{ "--i": i }}
            onClick={() => choose(l.code)}
            aria-label={names[l.code]}
            title={names[l.code]}
            tabIndex={open ? 0 : -1}
          >
            <span className="lang-flag" aria-hidden="true">{l.flag}</span>
            <span className="lang-code">{l.code}</span>
          </button>
        ))}

        <button
          type="button"
          className={`lang-planet ${spinning ? "spin" : ""}`}
          onClick={() => setOpen((o) => !o)}
          onFocus={() => setOpen(true)}
          aria-haspopup="true"
          aria-expanded={open}
          aria-label={`${t("lang.label")}: ${names[active.code]}`}
        >
          <span className="lang-flag" aria-hidden="true">{active.flag}</span>
          <span className="lang-code">{active.code}</span>
        </button>
      </div>
    </div>
  );
};

export default LanguageSwitcher;
